/** Thread list rows: title, preview, time, unread badge, archived marker. */
import type { ThreadDto } from '@oex/contracts';
import { DateText, cn } from '@oex/ui';
import { Archive, BellOff, MessageCircle } from 'lucide-react';
import { useT } from '../../../app/i18n';
import { useSession } from '../../../app/session';
import { useUiSettings } from '../../../app/ui-settings';
import { threadTitle } from './labels';

export function UnreadBadge({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={label}
      className="font-data inline-flex min-w-5 items-center justify-center rounded-full bg-[var(--oex-purple)] px-1.5 text-[11px] font-semibold text-white"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function ThreadList({
  threads,
  selected,
  onSelect,
}: {
  threads: ThreadDto[];
  selected?: string | null;
  onSelect(id: string): void;
}) {
  const t = useT();
  const { me, activeOrgId } = useSession();
  const { settings } = useUiSettings();
  const muted = new Set(settings.mutedThreadIds ?? []);
  return (
    <ul className="flex flex-col" aria-label={t('messaging.threads.label')}>
      {threads.map((th) => (
        <li key={th.id}>
          <button
            type="button"
            data-thread-row={th.id}
            aria-current={th.id === selected ? 'true' : undefined}
            onClick={() => onSelect(th.id)}
            className={cn(
              'flex w-full items-start gap-2 border-b border-border px-2 py-2 text-left hover:bg-accent signal-focus',
              th.id === selected ? 'bg-accent' : '',
            )}
          >
            {th.archivedAt ? (
              <Archive className="mt-0.5 size-4 text-muted-foreground" aria-hidden />
            ) : (
              <MessageCircle className="mt-0.5 size-4 text-muted-foreground" aria-hidden />
            )}
            <span className="min-w-0 flex-1">
              <span className={cn('block truncate text-sm', th.unreadCount > 0 ? 'font-semibold' : '')}>
                {threadTitle(t, th, me?.user.id ?? null, activeOrgId)}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {th.lastMessagePreview ?? t('messaging.thread.noMessages')}
              </span>
            </span>
            <span className="flex flex-col items-end gap-1">
              {th.lastMessageAt ? (
                <DateText value={th.lastMessageAt} className="text-[11px] text-muted-foreground" />
              ) : null}
              {muted.has(th.id) ? (
                <BellOff className="size-3.5 text-muted-foreground" aria-label={t('messaging.thread.muted')} />
              ) : (
                <UnreadBadge count={th.unreadCount} label={t('messaging.unread', { count: th.unreadCount })} />
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
